import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import { max, min, nonNullable } from 'lib/array/arrayUtils'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'

export const isPayFullyNowAvailable = (offer: App.CarHireOffer) => {
  return !!offer.rateOptions.find((option) => !!option.payNowAmount && !option.payOnArrivalAmount)
}

export const isPayFullyOnArrivalAvailable = (offer: App.CarHireOffer) => {
  return !!offer.rateOptions.find((option) => !option.payNowAmount && !!option.payOnArrivalAmount)
}

export const isPayPartiallyAvailable = (offer: App.CarHireOffer) => {
  return !!offer.rateOptions.find((option) => !!option.payNowAmount && !!option.payOnArrivalAmount)
}

export const getRateOptionByRateType = (offer: App.CarHireOffer, rateOptionType: App.CarHireRateOptionTypes) => {
  return offer.rateOptions.find((option) => {
    return (rateOptionType === 'payFullyNow' && !!option.payNowAmount && !option.payOnArrivalAmount) ||
      (rateOptionType === 'payFullyOnArrival' && !option.payNowAmount && !!option.payOnArrivalAmount) ||
      (rateOptionType === 'payPartiallyNow' && !!option.payNowAmount && !!option.payOnArrivalAmount)
  })
}

export const getRateOptionType = (option: App.CarHireOffer['rateOptions'][number]): App.CarHireRateOptionTypes => {
  if (!!option.payNowAmount && !option.payOnArrivalAmount) return 'payFullyNow'
  if (!option.payNowAmount && !!option.payOnArrivalAmount) return 'payFullyOnArrival'
  return 'payPartiallyNow'
}

const BUNDLED_CAR_HIRE_ORDER_ELIGIBLE_ITEM_STATUS: Set<App.OrderItemStatus> = new Set(['completed', 'created', 'pending', 'processing'])
export const getOrderItemsEligibleForCarHireBundledDiscount = (order: App.Order, offers: Array<App.AnyOffer>) => {
  const now = new Date()
  const hotels: Array<{
    startDate: string,
    endDate: string,
    latitude: number,
    longitude: number,
    countryCode: string,
    offer: App.OrderItemOffer | App.OrderBedbankOffer,
  }> = [
    // Bundled car hire is only available for LPC for now
    ...nonNullable(order.items.map(item => {
      const hotelOffer = offers.find(offer => offer.id === item.offer.id)
      if (isLpcHotelOffer(hotelOffer) && item.offer.property && item.reservation) {
        return {
          startDate: item.reservation.startDate,
          endDate: item.reservation.endDate,
          latitude: hotelOffer.property.latitude,
          longitude: hotelOffer.property.longitude,
          countryCode: hotelOffer.property.geoData.countryCode ?? '',
          offer: item.offer,
          status: item.status,
        }
      }
    })),
  ].filter(item =>
    // Only include items that are with accepted status
    BUNDLED_CAR_HIRE_ORDER_ELIGIBLE_ITEM_STATUS.has(item.status) &&
    // and are within the reservation period
    dateIsBeforeOrSame(now, new Date(item.endDate)),
  )

  const earliestItem = min(hotels, item => new Date(item.startDate))
  const latestItem = max(hotels, item => new Date(item.endDate))

  const isWithinReservationPeriod = !!latestItem && new Date() <= new Date(latestItem.endDate)

  return {
    earliestItem: isWithinReservationPeriod ? earliestItem : undefined,
    latestItem: isWithinReservationPeriod ? latestItem : undefined,
    items: hotels,
  }
}
